import type { Edge, Node } from 'reactflow';
import {
  assetById,
  featuredThenRest,
  filterPolicies,
  inheritedModels,
  matchedAssets,
  matchingColumns,
  modelById,
  overviewPolicies,
  ownerName,
  policyById,
  policyCoverage,
  vaultById,
} from './policyGraphResolve';
import type {
  GraphCatalog,
  GraphContext,
  GraphNodeData,
  GraphPolicy,
  GraphPolicyStatus,
  GraphPolicyType,
  GraphRelation,
  OwnershipLevel,
} from './policyGraphTypes';
import { EXPANSION_CAP, GRAPH_LAYER_Y, GRAPH_NODE_GAP_X, GRAPH_NODE_H, GRAPH_NODE_W, POLICY_TYPE_LABEL } from './policyGraphTypes';

export interface GraphViewFilters {
  query: string;
  type: 'all' | GraphPolicyType;
  ownership: 'all' | OwnershipLevel;
  status: 'all' | GraphPolicyStatus;
}

export interface GraphViewState {
  context: GraphContext;
  expanded: Set<string>;
  selectedPolicyId: string | null;
  filters: GraphViewFilters;
}

export { GRAPH_NODE_H, GRAPH_NODE_W };

export function expandKey(kind: string, id: string, extra = ''): string {
  return extra ? `${kind}:${id}:${extra}` : `${kind}:${id}`;
}

export function primaryExpandKey(kind: 'policy' | 'model' | 'asset', id: string, policy?: GraphPolicy): string | null {
  if (kind === 'policy') {
    if (policy?.ownership.level === 'VAULT') return expandKey('policy', id, 'inherited');
    return expandKey('policy', id, 'protects');
  }
  if (kind === 'model') return expandKey('model', id, 'assets');
  return expandKey('asset', id, 'columns');
}

export function expandKeysForNode(nodeId: string): string[] {
  const parsed = nodeKindFromId(nodeId);
  if (parsed.kind === 'policy') {
    return [
      expandKey('policy', parsed.rest, 'inherited'),
      expandKey('policy', parsed.rest, 'protects'),
      expandKey('policy', parsed.rest, 'depends'),
    ];
  }
  if (parsed.kind === 'model') return [expandKey('model', parsed.rest, 'assets')];
  if (parsed.kind === 'asset') return [expandKey('asset', parsed.rest, 'columns')];
  return [];
}

export function collectExpandKeys(
  catalog: GraphCatalog,
  context: GraphContext,
  filters: GraphViewFilters,
  depth: 2 | 'all' = 2,
): Set<string> {
  const policies = filterPolicies(catalog, overviewPolicies(catalog, context), filters);
  const keys = new Set<string>();
  policies.forEach((policy) => {
    if (policy.ownership.level === 'VAULT') keys.add(expandKey('policy', policy.id, 'inherited'));
  });
  if (depth !== 'all') return keys;

  const assetIds = new Set<string>();
  policies.forEach((policy) => {
    keys.add(expandKey('policy', policy.id, 'protects'));
    if (policy.dependsOnAssetId) {
      keys.add(expandKey('policy', policy.id, 'depends'));
      assetIds.add(policy.dependsOnAssetId);
    }
    if (policy.ownership.modelId) keys.add(expandKey('model', policy.ownership.modelId, 'assets'));
    inheritedModels(catalog, policy).forEach((model) => keys.add(expandKey('model', model.id, 'assets')));
    featuredThenRest(matchedAssets(catalog, policy)).slice(0, EXPANSION_CAP).forEach((asset) => assetIds.add(asset.id));
    if (policy.ownership.assetId) assetIds.add(policy.ownership.assetId);
  });
  if (context.modelId) keys.add(expandKey('model', context.modelId, 'assets'));
  if (context.assetId) assetIds.add(context.assetId);
  assetIds.forEach((id) => keys.add(expandKey('asset', id, 'columns')));
  return keys;
}

function layerOf(id: string, kind: GraphNodeData['kind']): number {
  if (kind === 'vault') return 0;
  if (kind === 'policy') return 1;
  if (kind === 'model') return 2;
  if (kind === 'asset') return 3;
  if (kind === 'column') return 4;
  if (id.includes(':columns')) return 4;
  return 3;
}

function nodeOf(id: string, data: GraphNodeData): Node<GraphNodeData> {
  return {
    id,
    type: 'policyGraph',
    position: { x: 0, y: 0 },
    data,
    draggable: false,
    selectable: true,
    style: { width: GRAPH_NODE_W, height: GRAPH_NODE_H },
    zIndex: 2,
  };
}

function edgeOf(source: string, target: string, relation: GraphRelation, dashed = false, dotted = false): Edge {
  const overlap = relation === 'overlaps_with';
  return {
    id: `${relation}:${source}:${target}`,
    source,
    target,
    type: 'smoothstep',
    sourceHandle: overlap ? 'right' : 'bottom-source',
    targetHandle: overlap ? 'left' : 'top-target',
    style: {
      stroke: '#9CA3AF',
      strokeWidth: 1,
      ...(dashed ? { strokeDasharray: '5 4' } : {}),
      ...(dotted ? { strokeDasharray: '1.5 3' } : {}),
    },
  };
}

function policyMeta(catalog: GraphCatalog, policy: GraphPolicy): string {
  const coverage = policyCoverage(catalog, policy);
  const status = policy.status === 'ACTIVE' ? 'Active' : 'Draft';
  return `${POLICY_TYPE_LABEL[policy.type]} · ${status} · ${coverage.assetsMatched} matched`;
}

function applyLayout(nodes: Node<GraphNodeData>[], edges: Edge[]): Node<GraphNodeData>[] {
  const order = new Map(nodes.map((node, index) => [node.id, index]));
  const layerIndex = new Map(nodes.map((node) => [node.id, layerOf(node.id, node.data.kind)]));
  const byLayer: Node<GraphNodeData>[][] = [[], [], [], [], []];
  nodes.forEach((node) => {
    byLayer[layerIndex.get(node.id) ?? 0].push(node);
  });

  const xOf = new Map<string, number>();
  byLayer.forEach((layerNodes, layer) => {
    const ranked = [...layerNodes].sort((left, right) => (order.get(left.id) ?? 0) - (order.get(right.id) ?? 0));
    const scored = layerNodes.map((node) => {
      const parentXs = edges
        .filter((edge) => edge.target === node.id && !String(edge.id).startsWith('overlaps_with:'))
        .flatMap((edge) => {
          const sourceLayer = layerIndex.get(edge.source);
          const x = xOf.get(edge.source);
          if (sourceLayer === undefined || x === undefined || sourceLayer >= layer) return [];
          return [x];
        });
      const rank = ranked.findIndex((item) => item.id === node.id);
      const desired = parentXs.length
        ? parentXs.reduce((sum, value) => sum + value, 0) / parentXs.length
        : rank * (GRAPH_NODE_W + GRAPH_NODE_GAP_X);
      return { node, desired };
    });
    scored.sort((left, right) => left.desired - right.desired || (order.get(left.node.id) ?? 0) - (order.get(right.node.id) ?? 0));
    let nextX = 0;
    scored.forEach(({ node, desired }) => {
      const x = Math.max(desired, nextX);
      xOf.set(node.id, x);
      nextX = x + GRAPH_NODE_W + GRAPH_NODE_GAP_X;
    });
  });

  const rights = byLayer.map((layerNodes) => {
    if (!layerNodes.length) return GRAPH_NODE_W;
    return Math.max(...layerNodes.map((node) => (xOf.get(node.id) ?? 0) + GRAPH_NODE_W));
  });
  const maxRight = Math.max(...rights, GRAPH_NODE_W);
  const mins = byLayer.map((layerNodes) => {
    if (!layerNodes.length) return 0;
    return Math.min(...layerNodes.map((node) => xOf.get(node.id) ?? 0));
  });

  return nodes.map((node) => {
    const layer = layerIndex.get(node.id) ?? 0;
    const width = rights[layer] - mins[layer];
    const offset = (maxRight - width) / 2 - mins[layer];
    return {
      ...node,
      position: { x: (xOf.get(node.id) ?? 0) + offset + 32, y: GRAPH_LAYER_Y[layer] },
      style: { width: GRAPH_NODE_W, height: GRAPH_NODE_H },
      zIndex: 2,
    };
  });
}

function routeEdges(nodes: Node<GraphNodeData>[], edges: Edge[]): Edge[] {
  const byId = new Map(nodes.map((node) => [node.id, node]));
  return edges.map((edge) => {
    if (!String(edge.id).startsWith('overlaps_with:')) {
      return { ...edge, sourceHandle: 'bottom-source', targetHandle: 'top-target' };
    }
    const source = byId.get(edge.source);
    const target = byId.get(edge.target);
    if (!source || !target) return edge;
    if (source.position.x <= target.position.x) {
      return { ...edge, sourceHandle: 'right', targetHandle: 'left' };
    }
    return { ...edge, sourceHandle: 'left-source', targetHandle: 'right-target' };
  });
}

export function buildGraphView(catalog: GraphCatalog, state: GraphViewState): { nodes: Node<GraphNodeData>[]; edges: Edge[] } {
  const vault = vaultById(catalog, state.context.vaultId);
  const policies = filterPolicies(catalog, overviewPolicies(catalog, state.context), state.filters);
  const selectedPolicy = state.selectedPolicyId ? policyById(catalog, state.selectedPolicyId) : undefined;
  const nodes: Node<GraphNodeData>[] = [];
  const edges: Edge[] = [];
  const present = new Set<string>();

  const addNode = (node: Node<GraphNodeData>) => {
    if (present.has(node.id)) return;
    present.add(node.id);
    nodes.push(node);
  };

  if (vault) {
    addNode(nodeOf(`vault:${vault.id}`, {
      kind: 'vault',
      title: vault.name,
      subtitle: 'DataVault',
      meta: `${policies.length} ${policies.length === 1 ? 'policy' : 'policies'} in view`,
      expandable: false,
      expanded: false,
    }));
  }

  const showModel = (modelId: string, fromPolicyId?: string) => {
    const model = modelById(catalog, modelId);
    if (!model) return;
    const key = expandKey('model', model.id, 'assets');
    addNode(nodeOf(`model:${model.id}`, {
      kind: 'model',
      title: model.name,
      subtitle: 'Data Model',
      meta: `${model.assetIds.length} assets in access context`,
      expandable: true,
      expanded: state.expanded.has(key),
    }));
    if (fromPolicyId) edges.push(edgeOf(`policy:${fromPolicyId}`, `model:${model.id}`, 'inherited_by', true));
  };

  const showAsset = (assetId: string, from: { policyId?: string; modelId?: string }) => {
    const asset = assetById(catalog, assetId);
    if (!asset) return;
    const key = expandKey('asset', asset.id, 'columns');
    addNode(nodeOf(`asset:${asset.id}`, {
      kind: 'asset',
      title: asset.name,
      subtitle: asset.source,
      meta: `${asset.source} · ${asset.columns.length} columns`,
      expandable: true,
      expanded: state.expanded.has(key),
    }));
    if (from.policyId) edges.push(edgeOf(`policy:${from.policyId}`, `asset:${asset.id}`, 'protects'));
    if (from.modelId) edges.push(edgeOf(`model:${from.modelId}`, `asset:${asset.id}`, 'inherited_by', true));
  };

  const showColumns = (assetId: string, policy?: GraphPolicy) => {
    const asset = assetById(catalog, assetId);
    if (!asset) return;
    const uniqueCols = policy
      ? policy.action.kind === 'filter'
        ? asset.columns.filter((column) => column.name === 'region' || column.name === 'consent')
        : matchingColumns(policy, asset)
      : asset.columns.filter((column) => ['PII', 'Confidential', 'PCI', 'Restricted'].includes(column.classification));
    uniqueCols.slice(0, EXPANSION_CAP).forEach((column) => {
      const id = `column:${asset.id}:${column.name}`;
      addNode(nodeOf(id, {
        kind: 'column',
        title: column.name,
        subtitle: `${asset.name} · ${column.type}`,
        meta: `${column.type} · ${column.classification}`,
        expandable: false,
        expanded: false,
      }));
      edges.push(edgeOf(`asset:${asset.id}`, id, 'protects'));
    });
    if (uniqueCols.length > EXPANSION_CAP) {
      const overflowId = `overflow:asset:${asset.id}:columns`;
      addNode(nodeOf(overflowId, {
        kind: 'overflow',
        title: `+${uniqueCols.length - EXPANSION_CAP} more columns`,
        subtitle: asset.name,
        meta: 'Open details to review the rest',
        expandable: false,
        expanded: false,
        overflowCount: uniqueCols.length - EXPANSION_CAP,
      }));
      edges.push(edgeOf(`asset:${asset.id}`, overflowId, 'protects'));
    }
  };

  const contextLevel = state.context.level === 'vault' ? 'VAULT' : state.context.level === 'model' ? 'MODEL' : 'ASSET';
  policies.forEach((policy) => {
    const inheritedKey = expandKey('policy', policy.id, 'inherited');
    const protectsKey = expandKey('policy', policy.id, 'protects');
    const inheritedHere = policy.ownership.level !== contextLevel;
    addNode(nodeOf(`policy:${policy.id}`, {
      kind: 'policy',
      title: policy.name,
      subtitle: `${POLICY_TYPE_LABEL[policy.type]} · ${ownerName(catalog, policy)}`,
      meta: inheritedHere
        ? `Inherited · ${policyMeta(catalog, policy)}`
        : `Local · ${policyMeta(catalog, policy)}`,
      expandable: true,
      expanded: state.expanded.has(inheritedKey) || state.expanded.has(protectsKey),
      origin: inheritedHere ? 'inherited' : 'local',
    }));
    if (policy.ownership.level === 'VAULT' && vault) {
      edges.push(edgeOf(`vault:${vault.id}`, `policy:${policy.id}`, 'owned_by'));
    }
    if (policy.ownership.level === 'MODEL' && policy.ownership.modelId) {
      showModel(policy.ownership.modelId);
      edges.push(edgeOf(`policy:${policy.id}`, `model:${policy.ownership.modelId}`, 'owned_by'));
    }
    if (policy.ownership.level === 'ASSET' && policy.ownership.assetId) {
      showAsset(policy.ownership.assetId, {});
      edges.push(edgeOf(`policy:${policy.id}`, `asset:${policy.ownership.assetId}`, 'owned_by'));
    }
  });

  policies.forEach((policy) => {
    if (state.expanded.has(expandKey('policy', policy.id, 'inherited'))) {
      inheritedModels(catalog, policy).forEach((model) => showModel(model.id, policy.id));
    }
    if (state.expanded.has(expandKey('policy', policy.id, 'protects'))) {
      const matched = featuredThenRest(matchedAssets(catalog, policy));
      matched.slice(0, EXPANSION_CAP).forEach((asset) => showAsset(asset.id, { policyId: policy.id }));
      if (matched.length > EXPANSION_CAP) {
        const overflowId = `overflow:policy:${policy.id}:protects`;
        addNode(nodeOf(overflowId, {
          kind: 'overflow',
          title: `+${matched.length - EXPANSION_CAP} more assets`,
          subtitle: policy.name,
          meta: 'Matching assets continue in details',
          expandable: false,
          expanded: false,
          overflowCount: matched.length - EXPANSION_CAP,
        }));
        edges.push(edgeOf(`policy:${policy.id}`, overflowId, 'protects'));
      }
    }
    if (state.expanded.has(expandKey('policy', policy.id, 'depends')) && policy.dependsOnAssetId) {
      showAsset(policy.dependsOnAssetId, {});
      edges.push(edgeOf(`policy:${policy.id}`, `asset:${policy.dependsOnAssetId}`, 'depends_on', false, true));
    }
  });

  catalog.models.forEach((model) => {
    const key = expandKey('model', model.id, 'assets');
    if (!state.expanded.has(key) || !present.has(`model:${model.id}`)) return;
    const inModel = (policy: GraphPolicy) => matchedAssets(catalog, policy).some((asset) => asset.modelIds.includes(model.id));
    const sourcePolicy = selectedPolicy && inModel(selectedPolicy)
      ? selectedPolicy
      : policies.find(inModel);
    const pool = sourcePolicy
      ? featuredThenRest(matchedAssets(catalog, sourcePolicy).filter((asset) => asset.modelIds.includes(model.id)))
      : featuredThenRest(catalog.assets.filter((asset) => asset.modelIds.includes(model.id)));
    pool.slice(0, EXPANSION_CAP).forEach((asset) => showAsset(asset.id, { modelId: model.id, policyId: sourcePolicy?.id }));
    if (pool.length > EXPANSION_CAP) {
      const overflowId = `overflow:model:${model.id}:assets`;
      addNode(nodeOf(overflowId, {
        kind: 'overflow',
        title: `+${pool.length - EXPANSION_CAP} more assets`,
        subtitle: model.name,
        meta: 'Matching assets continue in details',
        expandable: false,
        expanded: false,
        overflowCount: pool.length - EXPANSION_CAP,
      }));
      edges.push(edgeOf(`model:${model.id}`, overflowId, 'inherited_by', true));
    }
  });

  catalog.assets.forEach((asset) => {
    const key = expandKey('asset', asset.id, 'columns');
    if (!state.expanded.has(key) || !present.has(`asset:${asset.id}`)) return;
    showColumns(asset.id, selectedPolicy);
  });

  if (state.context.level === 'model' && state.context.modelId && !present.has(`model:${state.context.modelId}`)) {
    showModel(state.context.modelId);
  }
  if (state.context.level === 'asset' && state.context.assetId && !present.has(`asset:${state.context.assetId}`)) {
    showAsset(state.context.assetId, {});
  }

  const visiblePolicies = policies.map((policy) => policy.id);
  catalog.policies.forEach((policy) => {
    if (!visiblePolicies.includes(policy.id) || !policy.dependsOnAssetId) return;
    if (present.has(`asset:${policy.dependsOnAssetId}`)) {
      edges.push(edgeOf(`policy:${policy.id}`, `asset:${policy.dependsOnAssetId}`, 'depends_on', false, true));
    }
  });

  const overlapSeen = new Set<string>();
  policies.forEach((policy) => {
    const coverage = policyCoverage(catalog, policy);
    policies.forEach((other) => {
      if (policy.id >= other.id) return;
      const shared = coverage.matchedAssetIds.filter((id) => policyCoverage(catalog, other).matchedAssetIds.includes(id)
        || coverage.protectedColumns.some((item) => policyCoverage(catalog, other).protectedColumns.some((entry) => entry.assetId === item.assetId && entry.column === item.column)));
      if (!shared.length) return;
      const key = [policy.id, other.id].sort().join(':');
      if (overlapSeen.has(key)) return;
      overlapSeen.add(key);
      edges.push(edgeOf(`policy:${policy.id}`, `policy:${other.id}`, 'overlaps_with', true));
    });
  });

  const uniqueEdges = new Map<string, Edge>();
  edges.forEach((edge) => uniqueEdges.set(edge.id, edge));
  const placed = applyLayout(nodes, Array.from(uniqueEdges.values()));
  return { nodes: placed, edges: routeEdges(placed, Array.from(uniqueEdges.values())) };
}

export function nodeKindFromId(id: string): { kind: string; rest: string } {
  const [kind, ...rest] = id.split(':');
  return { kind, rest: rest.join(':') };
}

export { POLICY_TYPE_LABEL };
